import React from "react";
import { GlassCard, KpiCard } from "../components/GlassCard";
import { useBudget } from "../context/BudgetContext";
import { useI18n } from "../context/I18nContext";
import { formatCurrency, formatPercent } from "../lib/i18n";
import { CONSTANTS } from "../lib/constants";
import { CheckCircle2, AlertTriangle, XCircle, TrendingUp } from "lucide-react";
import { cn } from "../lib/utils";

const DiagBadge = ({ status, okLabel, koLabel, threshold, testId }) => {
  const ok = status === "ok";
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  return (
    <div
      data-testid={testId}
      className={cn(
        "flex items-start gap-3 p-4 rounded-xl border transition-all",
        ok
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-rose-50 border-rose-200 text-rose-800"
      )}
    >
      <Icon className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{ok ? okLabel : koLabel}</span>
        {threshold ? <span className="text-xs opacity-75">{threshold}</span> : null}
      </div>
    </div>
  );
};

const Gauge = ({ label, value, max, format, threshold, thresholdLabel, inverted = false, testId }) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const overThreshold = value > threshold;
  // "inverted" means higher is worse (debt ratio etc)
  const isOk = inverted ? !overThreshold : overThreshold;
  const color = isOk ? "bg-emerald-500" : "bg-rose-500";
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-sm font-medium text-indigo-950">{format(value)}</span>
      </div>
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-500", color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-400">
        {thresholdLabel}: {format(threshold)}
      </span>
    </div>
  );
};

export const AnalyseModule = () => {
  const { t, lang } = useI18n();
  const { indicators, diagnostic } = useBudget();

  const diagStyle = {
    saine: {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      text: "text-emerald-700",
      icon: CheckCircle2,
      label: t.analyse.diagnosticSaine,
    },
    surveiller: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-700",
      icon: AlertTriangle,
      label: t.analyse.diagnosticSurveiller,
    },
    fragile: {
      bg: "bg-rose-50",
      border: "border-rose-200",
      text: "text-rose-700",
      icon: XCircle,
      label: t.analyse.diagnosticFragile,
    },
  }[diagnostic.global];
  const DiagIcon = diagStyle.icon;

  const ravAccent = indicators.resteAVivre >= CONSTANTS.RESTE_A_VIVRE_MIN ? "emerald" : "rose";
  const tauxAccent = indicators.tauxEndettement <= CONSTANTS.TAUX_ENDETTEMENT_MAX ? "emerald" : "rose";

  return (
    <div className="flex flex-col gap-8" data-testid="module-analyse">
      <header className="flex flex-col gap-2">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
          {t.modules.analyse}
        </span>
        <h1 className="text-3xl sm:text-4xl tracking-tighter text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          {t.analyse.title}
        </h1>
        <p className="text-slate-500 text-base max-w-2xl">{t.analyse.subtitle}</p>
      </header>

      {/* Global diagnostic */}
      <div
        data-testid="diagnostic-global"
        className={cn(
          "rounded-2xl p-6 md:p-8 border flex items-center gap-5 shadow-sm",
          diagStyle.bg,
          diagStyle.border
        )}
      >
        <div className={cn("w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-sm", diagStyle.text)}>
          <DiagIcon className="w-7 h-7" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.analyse.diagnostic}</span>
          <span className={cn("text-2xl sm:text-3xl font-medium tracking-tight", diagStyle.text)} style={{ fontFamily: "Outfit, sans-serif" }}>
            {diagStyle.label}
          </span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard
          testId="kpi-revenus"
          label={t.analyse.revenusTotaux}
          value={formatCurrency(indicators.revenus, lang)}
          sub={t.revenus.perMonth}
          accent="emerald"
        />
        <KpiCard
          testId="kpi-depenses"
          label={t.analyse.depensesTotales}
          value={formatCurrency(indicators.depensesTotales, lang)}
          sub={t.revenus.perMonth}
          accent="indigo"
        />
        <KpiCard
          testId="kpi-rav"
          label={t.analyse.resteAVivre}
          value={formatCurrency(indicators.resteAVivre, lang)}
          sub={t.revenus.perMonth}
          accent={ravAccent}
        />
        <KpiCard
          testId="kpi-taux"
          label={t.analyse.tauxEndettement}
          value={formatPercent(indicators.tauxEndettement, lang)}
          sub={`${t.analyse.threshold}: ${formatPercent(CONSTANTS.TAUX_ENDETTEMENT_MAX, lang)}`}
          accent={tauxAccent}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <KpiCard
          testId="kpi-charges-fixes"
          label={t.analyse.chargesFixes}
          value={formatCurrency(indicators.chargesFixes, lang)}
          sub={formatPercent(indicators.poidsChargesFixes, lang)}
          accent="indigo"
        />
        <KpiCard
          testId="kpi-credits"
          label={t.analyse.mensualitesCredits}
          value={formatCurrency(indicators.credits, lang)}
          sub={formatPercent(indicators.tauxEndettement, lang)}
          accent="indigo"
        />
        <KpiCard
          testId="kpi-variables"
          label={t.analyse.depensesVariables}
          value={formatCurrency(indicators.variables, lang)}
          sub={formatPercent(indicators.poidsVariables, lang)}
          accent="indigo"
        />
      </div>

      {/* Borrowing capacity */}
      <GlassCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-indigo-950" />
          </div>
          <h2 className="text-lg font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
            {t.analyse.capaciteEmprunt}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="flex flex-col gap-1" data-testid="kpi-mensualite-max">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{t.analyse.mensualiteMax}</span>
            <span className="text-xl font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
              {formatCurrency(indicators.mensualiteMax, lang)}
            </span>
          </div>
          <div className="flex flex-col gap-1" data-testid="kpi-mensualite-dispo">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{t.analyse.mensualiteDispo}</span>
            <span className="text-xl font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
              {formatCurrency(indicators.mensualiteDispo, lang)}
            </span>
          </div>
          <div className="flex flex-col gap-1" data-testid="kpi-capacite">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{t.analyse.capaciteEmprunt}</span>
            <span className="text-2xl font-medium text-emerald-700" style={{ fontFamily: "Outfit, sans-serif" }}>
              {formatCurrency(indicators.capaciteEmprunt, lang)}
            </span>
          </div>
        </div>
      </GlassCard>

      {/* Gauges */}
      <GlassCard>
        <h2 className="text-lg font-medium text-indigo-950 mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
          {t.analyse.repartition}
        </h2>
        <div className="flex flex-col gap-5">
          <Gauge
            testId="gauge-endettement"
            label={t.analyse.tauxEndettement}
            value={indicators.tauxEndettement}
            max={0.5}
            format={(v) => formatPercent(v, lang)}
            threshold={CONSTANTS.TAUX_ENDETTEMENT_MAX}
            thresholdLabel={t.analyse.threshold}
            inverted
          />
          <Gauge
            testId="gauge-charges"
            label={t.analyse.poidsChargesFixes}
            value={indicators.poidsChargesFixes}
            max={0.7}
            format={(v) => formatPercent(v, lang)}
            threshold={CONSTANTS.POIDS_CHARGES_ALERTE}
            thresholdLabel={t.analyse.threshold}
            inverted
          />
          <Gauge
            testId="gauge-variables"
            label={t.analyse.poidsVariables}
            value={indicators.poidsVariables}
            max={0.6}
            format={(v) => formatPercent(v, lang)}
            threshold={CONSTANTS.POIDS_VARIABLES_ALERTE}
            thresholdLabel={t.analyse.threshold}
            inverted
          />
        </div>
      </GlassCard>

      {/* Diagnostic details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <DiagBadge
          testId="diag-endettement"
          status={diagnostic.endettement}
          okLabel={t.analyse.endettementCorrect}
          koLabel={t.analyse.endettementEleve}
          threshold={`${formatPercent(indicators.tauxEndettement, lang)} / ${formatPercent(CONSTANTS.TAUX_ENDETTEMENT_MAX, lang)}`}
        />
        <DiagBadge
          testId="diag-rav"
          status={diagnostic.rav}
          okLabel={t.analyse.ravCorrect}
          koLabel={t.analyse.ravFaible}
          threshold={`${formatCurrency(indicators.resteAVivre, lang)} / ${formatCurrency(CONSTANTS.RESTE_A_VIVRE_MIN, lang)}`}
        />
        <DiagBadge
          testId="diag-budget"
          status={diagnostic.budget === "ok" ? "ok" : "alerte"}
          okLabel={t.analyse.budgetCorrect}
          koLabel={t.analyse.budgetOptim}
        />
      </div>
    </div>
  );
};
